import React, { useState } from "react";
import {
  SearchBar,
  Button,
  GeneralMessage
} from "../../../../common/APICommon";
import { LoadingSpinner } from "../../../../common/Loading";
import { ConfirmationModal } from "../../../../common/Modal";
import EmployeeTransactionApproverTable from "./components/EmployeeTransactionApproverTable";
import EmployeeTransactionApproverModal from "./components/EmployeeTransactionApproverModal";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useEmployeeTransactionApprover } from "../../../hooks/useEmployeeTransactionApprover";

const EmployeeTransactionApprover = ({ user }) => {
  const {
    data, loading, showMessage, message, isSuccess,
    searchData, addApprover, updateApprover, removeApprover
  } = useEmployeeTransactionApprover(user);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const allowAdd = (data.length === 0);

  // ADDED: Confirmation Modal States
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: "",
    message: "",
    type: "info",
    onConfirm: () => { }
  });

  // MODIFIED: Edit handler
  const handleEdit = (item) => {
    setSelectedData(item);
    setIsModalOpen(true);
  };

  // ADDED: Delete handler with confirmation
  const handleDeleteClick = (id, fullName) => {
    setConfirmConfig({
      title: "Remove Approver",
      message: `Are you sure you want to remove ${fullName} from the approver list?`,
      type: "danger",
      confirmText: "Remove",
      onConfirm: async () => {
        await removeApprover(id, fullName);
        setShowConfirm(false);
      }
    });
    setShowConfirm(true);
  };

  // MODIFIED: Unified Save handler with confirmation
  const handleSaveTrigger = (...args) => {
    // args: [id, empId, newName, oldName] or [empId, newName]
    const fullName = selectedData ? args[2] : args[1];

    setConfirmConfig({
      title: selectedData ? "Update Approver" : "Confirm Addition",
      message: selectedData
        ? `Are you sure you want to change this approver to ${fullName}?`
        : `Are you sure you want to add ${fullName} as an approver?`,
      type: "info",
      confirmText: "Save",
      onConfirm: async () => {
        if (selectedData) {
          await updateApprover(...args);
        } else {
          await addApprover(...args);
        }
        setShowConfirm(false);
        setIsModalOpen(false); // Close the entry modal after confirmation
      }
    });
    setShowConfirm(true);
  };

  // ⚠️ Guard: If user is not passed, show a warning instead of crashing
  if (!user) {
    return (
      <div className="p-10 text-center text-red-500 font-bold bg-red-50 rounded-lg border border-red-200">
        Error: User context not found. Please ensure the parent component passes the 'user' prop.
      </div>
    );
  }

  return (
    <div className="p-2 space-y-4">
      <div className="flex justify-between items-center">
        <div className="w-64">
          <SearchBar handleFilter={searchData} />
        </div>
        {allowAdd && (
          <Button
            icon={<PersonAddIcon />}
            name={"Add Approver"}
            className={"bg-blue-600 hover:bg-blue-700 border-none cursor-pointer text-white shadow-md"}
            onClick={() => { setSelectedData(null); setIsModalOpen(true); }}
          />
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <EmployeeTransactionApproverTable
          data={data}
          onEdit={handleEdit}
          onDelete={handleDeleteClick} // MODIFIED: Uses confirm trigger
          loading={loading}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>

      {/* Main Entry Modal */}
      <EmployeeTransactionApproverModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedData={selectedData}
        onSave={handleSaveTrigger} // MODIFIED: Goes to confirmation first
        loading={loading}
      />

      {/* ADDED: Reusable Confirmation Modal */}
      <ConfirmationModal
        visible={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        type={confirmConfig.type}
        confirmText={confirmConfig.confirmText}
        isLoading={loading}
      />

      <LoadingSpinner loading={loading} />
      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
    </div>
  );
};

export default EmployeeTransactionApprover;